import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge, NativeSelect, Empty, slug } from "@/components/crm/common";

const STATUS = ["Open", "In Progress", "Blocked", "Done"];

function ActionRow({ a, onChanged, onEdit }) {
  const [note, setNote] = useState("");
  const t = `action-${a.id.slice(0, 6)}`;
  const patch = async (body) => {
    try { await api.patch(`/actions/${a.id}`, body); toast.success("Action updated"); setNote(""); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async () => { if (!window.confirm("Delete this action item?")) return; try { await api.delete(`/actions/${a.id}`); onChanged(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <div data-testid={t} className={`rounded-lg border p-3 ${a.overdue ? "border-rose-400/60" : "border-zinc-100"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><span className="font-bold text-zinc-900">{a.title}</span>{a.overdue && <span data-testid={`${t}-overdue`} className="flex items-center gap-1 text-[11px] font-bold text-rose-500"><AlertTriangle className="h-3 w-3" />Overdue{a.escalation_level ? ` · escalated to ${a.escalated_to}` : ""}</span>}</div>
          <div className="mt-1 text-xs text-zinc-500">{a.owner} · due {a.due} · {a.priority} · {a.meeting_title || a.project_name || "General"} · by {a.created_by}</div>
          {a.details && <p className="mt-1 text-xs text-zinc-600">{a.details}</p>}
          {a.evidence && <p className="mt-1 text-xs text-zinc-600">Evidence: {/^https?:/.test(a.evidence) ? <a href={a.evidence} target="_blank" rel="noreferrer" className="text-[#C5A46D] underline">{a.evidence}</a> : a.evidence}</p>}
        </div>
        <div className="flex items-center gap-1.5">
          {(a.mine || a.can_edit) ? <NativeSelect testid={`${t}-status`} value={a.status} onChange={(s) => patch({ status: s, note })} options={STATUS} /> : <StatusBadge value={a.status} />}
          {a.can_edit && <button data-testid={`${t}-edit`} onClick={() => onEdit(a)} className="p-1 text-zinc-400 hover:text-[#C5A46D]"><Pencil className="h-3.5 w-3.5" /></button>}
          {a.can_edit && <button data-testid={`${t}-delete`} onClick={del} className="p-1 text-zinc-400 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      </div>
      {(a.mine || a.can_edit) && a.status !== "Done" && (
        <div className="mt-2 flex gap-2">
          <input data-testid={`${t}-note`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Progress note or evidence link" className="h-8 flex-1 rounded-md border px-2 text-xs outline-none" />
          <button data-testid={`${t}-save-note`} disabled={!note} onClick={() => patch(/^https?:/.test(note) ? { evidence: note } : { note })} className="rounded-md bg-[#C5A46D] px-3 text-xs font-bold text-[#171923] disabled:opacity-40">Update</button>
        </div>
      )}
    </div>
  );
}

export function ActionsBoard({ items, filter, onChanged, onEdit }) {
  const rows = items.filter(filter);
  if (!rows.length) return <Empty text="No action items here." />;
  const groups = rows.reduce((g, a) => { (g[a.designation || "No designation"] ||= []).push(a); return g; }, {});
  return (
    <div className="space-y-5">
      {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([d, list]) => (
        <div key={d} data-testid={`designation-group-${slug(d)}`}>
          <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A46D]">{d}<span className="font-mono text-zinc-400">{list.length}</span>
            {list.some((a) => a.overdue) && <span className="text-rose-500">· {list.filter((a) => a.overdue).length} overdue</span>}</div>
          <div className="grid gap-2 lg:grid-cols-2">{list.map((a) => <ActionRow key={a.id} a={a} onChanged={onChanged} onEdit={onEdit} />)}</div>
        </div>
      ))}
    </div>
  );
}
